import BaseController from "confinus_ui/base_controller";

// Hydrates per-user heart state (marketplace grids are shared-cached, so the server
// cannot bake it in) and toggles favorites. Hearts opt in with:
//   <button data-favorites-sku-param="ABC-123" data-action="favorites#toggle">
// and show the solid state via the `is-favorited` class on the button.
export default class FavoritesController extends BaseController {
  public static values = {
    idsUrl: { type: String, default: "/marketplace/favorites/ids.json" },
    createUrl: { type: String, default: "/marketplace/favorites" },
  };

  declare private readonly idsUrlValue: string;
  declare private readonly createUrlValue: string;

  private skus: Set<string> = new Set();
  private observer: MutationObserver | null = null;
  private repaintScheduled = false;

  public connect(): void {
    super.connect();
    void this.loadAndPaint();
    document.addEventListener("turbo:load", this.repaint);
    // Hearts can enter the DOM after connect: lazy-loaded quick-view modals, turbo
    // frames, infinite-scroll grids. Repaint when new nodes are added. We watch
    // childList only (not attributes), so our own is-favorited toggling can't loop.
    this.observer = new MutationObserver(this.onMutations);
    this.observer.observe(document.body, { childList: true, subtree: true });
  }

  public disconnect(): void {
    document.removeEventListener("turbo:load", this.repaint);
    this.observer?.disconnect();
    this.observer = null;
    super.disconnect();
  }

  private readonly onMutations = (mutations: MutationRecord[]): void => {
    if (mutations.some((m) => m.addedNodes.length > 0)) this.repaint();
  };

  private async loadAndPaint(): Promise<void> {
    try {
      const res = await fetch(this.idsUrlValue, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      });
      if (!res.ok) return;
      const data = (await res.json()) as { skus: string[] };
      this.skus = new Set(data.skus);
      this.paintAll();
    } catch {
      // Degraded: leave hearts outline; toggling still works.
    }
  }

  // Debounced via rAF so a burst of DOM mutations coalesces into one paint pass.
  private readonly repaint = (): void => {
    if (this.repaintScheduled) return;
    this.repaintScheduled = true;
    requestAnimationFrame(() => {
      this.repaintScheduled = false;
      this.paintAll();
    });
  };

  private paintAll(): void {
    this.heartButtons().forEach((btn) => this.paint(btn));
  }

  private heartButtons(): HTMLElement[] {
    return Array.from(
      document.querySelectorAll<HTMLElement>("[data-favorites-sku-param]"),
    );
  }

  private skuFor(el: HTMLElement): string {
    return el.dataset.favoritesSkuParam ?? "";
  }

  private paint(btn: HTMLElement): void {
    const on = this.skus.has(this.skuFor(btn));
    btn.classList.toggle("is-favorited", on);
    btn.setAttribute("aria-pressed", on ? "true" : "false");
  }

  public async toggle(event: Event): Promise<void> {
    event.preventDefault();
    event.stopPropagation();
    const btn = event.currentTarget as HTMLElement;
    const sku = this.skuFor(btn);
    if (!sku) return;

    const wasOn = this.skus.has(sku);
    if (wasOn) {
      this.skus.delete(sku);
    } else {
      this.skus.add(sku);
    }
    this.paintAll();

    const ok = await this.persist(sku, !wasOn);
    if (!ok) {
      if (wasOn) {
        this.skus.add(sku);
      } else {
        this.skus.delete(sku);
      }
      this.paintAll();
    }
  }

  private async persist(sku: string, add: boolean): Promise<boolean> {
    try {
      const res = await fetch(this.createUrlValue, {
        method: add ? "POST" : "DELETE",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken,
          Accept: "application/json",
        },
        credentials: "same-origin",
        body: JSON.stringify({ sku }),
      });
      return res.ok;
    } catch {
      return false;
    }
  }

  private get csrfToken(): string {
    const meta = document.querySelector('meta[name="csrf-token"]');
    return meta ? (meta.getAttribute("content") ?? "") : "";
  }
}
